import { EditorState } from 'draft-js';
import { createRef, PureComponent } from 'react';

import Latex from 'my-elements/Latex';

import EquationEditor from './EquationEditor';

const EDITOR_PADDING = 0;

class EquationDecorator extends PureComponent {
  state = {
    showEditor: false,
    width: '1em',
    height: '1.1em',
    showEquation: false,
  };
  rootEl = createRef();
  equationEl = createRef();

  componentDidMount() {
    if (!this.props.readOnly && this.getEntityData().init) {
      this.updateEntityData({ latex: '' });
      this.showEditor();
    }
  }

  recalculateSize = () => {
    setTimeout(() => {
      const el = this.equationEl.current;
      if (el && !this.state.showEditor) {
        const height =
          [...el.querySelectorAll('.base')].reduce((max, el) => (el.clientHeight > max ? el.clientHeight : max), 0) ||
          el.clientHeight;
        this.setState({
          width: el.offsetWidth + 1 + EDITOR_PADDING * 2,
          height,
          showEquation: true,
        });
      }
    }, 50);
  };

  updateEntityData = data => {
    const { entityKey, getEditorState, setEditorState } = this.props;
    const editorState = getEditorState();
    // decorator does not get re-rendered every time editor contentState changes, so the contentState
    // prop may be stale.  Therefore we use the getEditorState method to fetch the  the most up-to-date
    // editor state with its current contentState.
    // (this fixed bug where after adding inline equation then couple more blocks below, then changing
    //  the inline equation above removed the new blocks added after equation was created)
    const updatedContentState = editorState.getCurrentContent().replaceEntityData(entityKey, data);
    setEditorState(EditorState.push(editorState, updatedContentState, 'apply-entity'), true);
  };

  updateLatex = latex => this.updateEntityData({ latex });

  showEditor = () => {
    this.props.setReadOnly(true);
    this.props.toggleNestedEditorFocus?.(true);
    this.setState({ showEditor: true, showEquation: false });
  };

  closeEditor = () => {
    // if (['click', 'keydown'].includes(e.type)) {
    //   this.props.focusAfter()
    // }
    this.props.setReadOnly(false);
    this.props.onResetFocus?.();
    this.props.toggleNestedEditorFocus?.(false);
    this.recalculateSize();
    this.setState({ showEditor: false });
  };

  getEntityData() {
    const { contentState, entityKey } = this.props;
    return contentState.getEntity(entityKey).getData();
  }

  render() {
    const { children, readOnly, reportLatexErrors } = this.props;
    const { height, showEditor, showEquation, width } = this.state;
    const { latex } = this.getEntityData();

    if (readOnly) {
      const styleSet = children?.[0]?.props?.styleSet;
      if (!styleSet || styleSet.isEmpty() || !styleSet.some(s => s.startsWith('HIGHLIGHT')))
        return <Latex reportErrors={reportLatexErrors}>{latex}</Latex>;
      const styleMap = children[0].props.customStyleMap;
      return (
        <span style={styleSet.reduce((mem, style) => (Object.assign(mem, styleMap[style]), mem), {})}>
          <Latex reportErrors={reportLatexErrors}>{latex}</Latex>
        </span>
      );
    }

    // in 'edit' mode, we need to wrap the latex in an absolute positioned wrapper so it doesn't interfere with cursor placement
    return (
      <span css={rootCss} ref={this.rootEl} style={{ width, height }}>
        <span>{children}</span>
        <div className="ED-equationWrapper" style={showEquation ? { width, height } : {}}>
          <div className="ED-equation" contentEditable={false} readOnly>
            <Latex onClick={this.showEditor} onRendered={this.recalculateSize} ref={this.equationEl}>
              {latex}
            </Latex>
          </div>
        </div>
        {showEditor && (
          <EquationEditor
            anchorEl={this.rootEl.current}
            onClose={this.closeEditor}
            onUpdate={this.updateLatex}
            value={latex}
          />
        )}
      </span>
    );
  }
}

const rootCss = {
  display: 'inline-block',
  padding: `0 ${EDITOR_PADDING}px`,
  background: 'rgba(0,0,0,.05)',
  position: 'relative',
  '.ED-equationWrapper': {
    position: 'absolute',
    overflow: 'visible',
    width: 1000,
    left: EDITOR_PADDING,
    top: 0,
  },
  '.ED-equation': {
    userSelect: 'none',
    cursor: 'pointer',
  },
};

export default {
  strategy: (contentBlock, callback, contentState) => {
    contentBlock.findEntityRanges(character => {
      const entityKey = character.getEntity();
      return entityKey !== null && contentState.getEntity(entityKey).getType() === 'EQUATION';
    }, callback);
  },
  component: EquationDecorator,
};
